import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { TypedMap } from 'flow/types/TypedMap';
import { ChatUserKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
// Aliased: this module also exports its own (resolved-for-display) MeetingMember.
import { MeetingMember as MeetingMemberRecord } from 'flow/types/Meeting';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import { getFullNameFromContact } from 'flow/utils/call-utils-TW';
import {
    selectVideoMembers,
    selectVideoSenderVideoActive,
} from 'flow/selectors/video-selectors';
import {
    setActiveMeetingMemberListActiveStatus,
    getContactFromNumber,
} from 'flow/actions/meeting-actions';
import {
    selectSenderName,
    selectSenderAudioActive,
    selectActiveMeetingMembers,
    selectActiveMeetingChatSessionId,
    selectActiveMeetingChatRoomMemberChatUserKeys,
    selectConferenceMeetingCallingMembers,
    deriveMeetingMemberGuestInfo,
} from 'flow/selectors/meeting-selectors';
import { CALLING_DURATION } from 'flow/utils/meeting-constants';

interface MemberExtension {
    extension?: TypedMap<Extension>;
    leftTime?: number | null;
    member?: TypedMap<MeetingMemberRecord>;
}

interface CallingMemberExtension {
    extension?: TypedMap<Extension>;
    callingEndTime: number;
    member?: TypedMap<MeetingMemberRecord>;
}

interface MemberContact {
    member: TypedMap<MeetingMemberRecord>;
    // Resolved from the untyped getContactFromNumber action, hence any.
    contact: any;
    memberKey: string;
}

/** A participant currently in the meeting, resolved for display. */
export interface MeetingMember {
    memberKey: string;
    name: string;
    displayName: string;
    /** Resolved to a person by the avatar, which is the only consumer. */
    phoneNumber: string;
    callerId: string;
    memberIsSelf: boolean;
    memberIsCaller: boolean;
    /** True when the participant is live in the meeting but not a permanent chat room member — internal or external. */
    isGuest: boolean;
    /** True when the participant resolves to a Telavox extension in this org (a colleague), false for external participants (e.g. dialed-in numbers). */
    isInternal: boolean;
    /** Set only for an internal participant (isInternal) — needed to invite a guest into the room. */
    chatUserKey: ChatUserKey | undefined;
    videoActive: boolean;
    audioActive: boolean;
}

/** A colleague being rung in (outgoing invite) that still warrants a row. */
export interface CallingMember {
    key: string;
    name: string;
    contact: any;
}

interface MeetingMembers {
    members: MeetingMember[];
    /** Non-guest members — single pass over `members`, shared by every consumer that needs the room/guest split (previously duplicated as members.filter(...) in each). */
    roomMembers: MeetingMember[];
    /** Guest members — see roomMembers. */
    guestMembers: MeetingMember[];
    callingMembers: CallingMember[];
    count: number;
    closeMembersList: () => void;
}

/**
 * Resolves the active meeting's participants and outgoing-invite ("calling…")
 * rows for the participants panel. Owns the contact/extension bookkeeping that
 * decides which calling rows are still worth showing (an invite is dropped once
 * the invitee is already in the meeting, or once the ring is stale — they left
 * less than CALLING_DURATION ago; see the callingMembers memo below).
 *
 * NOTE: this derivation logic was extracted from the legacy MeetingMembersList,
 * then tightened (real types, memoised outputs, complete dependency arrays) with
 * behaviour preserved. Two legacy quirks remain: the resolved `contact` is `any`
 * because getContactFromNumber is an untyped JS action, and a couple of memos
 * aren't pure — meetingMemberContacts dispatches getContactFromNumber and
 * meetingMemberExtensions reads Date.now() during render.
 */
const useMeetingMembers = (): MeetingMembers => {
    const dispatch = useAppDispatch();

    const ownName = useAppSelector(selectSenderName);
    const meetingMembers = useAppSelector(selectActiveMeetingMembers);
    const videoMembers = useAppSelector(selectVideoMembers);
    // videoMembers has no live entry for ourselves, so read our own state directly.
    const ownAudioActive = useAppSelector(selectSenderAudioActive);
    const ownVideoActive = useAppSelector(selectVideoSenderVideoActive);
    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );
    const callingParticipants = useAppSelector(
        selectConferenceMeetingCallingMembers,
    );
    const chatSessionId = useAppSelector(selectActiveMeetingChatSessionId);
    const chatRoomMemberKeys = useAppSelector(
        selectActiveMeetingChatRoomMemberChatUserKeys,
    );

    const getContactFromNumberAction = useCallback(
        (number: string) => dispatch(getContactFromNumber(number)),
        [dispatch],
    );

    const [allMemberExtensions, setAllMemberExtensions] = useState<
        MemberExtension[]
    >([]);

    const closeMembersList = () =>
        dispatch(setActiveMeetingMemberListActiveStatus(false));

    const meetingMemberContacts = useMemo(
        () =>
            meetingMembers
                .map((member) => {
                    const number = member.get('number');
                    return {
                        member,
                        contact: number
                            ? getContactFromNumberAction(number)
                            : undefined,
                        memberKey: member.get('key'),
                    };
                })
                .toArray(),
        [meetingMembers, getContactFromNumberAction],
    );

    const meetingMemberExtensions = useMemo(
        () =>
            meetingMemberContacts?.map(({ member, contact }: MemberContact) => {
                const name = member.get('displayName');
                const videoParticipant = videoMembers.get(name);
                const isCallerParticipant =
                    !videoParticipant && name !== ownName;

                const extension = extensions?.find(
                    (ext) =>
                        contact?.get('key') === ext.getIn(['contact', 'key']),
                );

                return {
                    extension,
                    leftTime: isCallerParticipant ? Date.now() : null,
                    member,
                };
            }),
        [extensions, videoMembers, ownName, meetingMemberContacts],
    );

    const callingExtensions = useMemo(() => {
        // Defined inside the memo so it isn't a phantom dependency.
        const createCallingExtension = ({
            extensionKey,
            callingEndTime,
        }: {
            extensionKey: string;
            callingEndTime: number;
        }) => {
            const extension = extensions?.find(
                (ext) => extensionKey === ext.get('key'),
            );
            const member = meetingMemberExtensions.find(
                (meetingMember) =>
                    meetingMember?.extension?.get('key') === extensionKey,
            )?.member;

            return { extension, callingEndTime, member };
        };

        return callingParticipants?.map(createCallingExtension).toArray() || [];
    }, [callingParticipants, extensions, meetingMemberExtensions]);

    // Accumulate member extensions across renders: keep prior entries that
    // aren't currently present (so a member who just left is still available
    // for the short-call check), then append the current ones. A Set membership
    // test replaces a nested find — keeping the callback nesting shallow.
    useEffect(() => {
        const currentExtensionKeys = new Set(
            meetingMemberExtensions.map((memberExtension) =>
                memberExtension?.extension?.get('key'),
            ),
        );
        setAllMemberExtensions((prev) => [
            ...prev.filter(
                (participant: MemberExtension) =>
                    !currentExtensionKeys.has(
                        participant?.extension?.get('key'),
                    ),
            ),
            ...meetingMemberExtensions,
        ]);
    }, [meetingMemberExtensions]);

    // Memoised so the arrays (and the member objects inside) keep stable
    // references across re-renders that don't change the underlying data — the
    // panel re-renders whenever its ancestor MeetingVideoCanvas does (resize,
    // speaking detection, …), and the React.memo'd rows skip those re-renders
    // only while these references hold.
    const members: MeetingMember[] = useMemo(
        () =>
            meetingMembers
                .sortBy((participant) => participant.get('joinTime'))
                .toArray()
                .map((member) => {
                    const name = member.get('displayName');
                    const memberKey = member.get('key');
                    const videoParticipant = videoMembers.get(name);
                    const memberIsCaller =
                        !videoParticipant && name !== ownName;
                    const memberIsSelf = name === ownName;

                    const {
                        isGuest: isGuestCandidate,
                        isInternal,
                        chatUserKey,
                    } = deriveMeetingMemberGuestInfo(
                        member.get('identity'),
                        chatSessionId,
                        chatRoomMemberKeys,
                    );
                    const isGuest = isGuestCandidate && !memberIsSelf;

                    return {
                        memberKey,
                        name,
                        displayName: sliceUUIDFromUserName(name),
                        phoneNumber: member.get('number'),
                        callerId: member.get('callerId'),
                        memberIsSelf,
                        memberIsCaller,
                        isGuest,
                        isInternal,
                        chatUserKey,
                        videoActive: memberIsSelf
                            ? ownVideoActive
                            : !!videoParticipant?.get('videoActive'),
                        audioActive: memberIsSelf
                            ? ownAudioActive
                            : !!videoParticipant?.get('audioActive'),
                    };
                }),
        [
            meetingMembers,
            videoMembers,
            ownName,
            ownAudioActive,
            ownVideoActive,
            chatSessionId,
            chatRoomMemberKeys,
        ],
    );

    // The two row-suppression checks are inlined (rather than separate helpers)
    // so the memo's dependency array honestly lists everything it reads:
    // callingExtensions, meetingMemberContacts (already-in-meeting) and
    // allMemberExtensions (recently-left / short call).
    const callingMembers: CallingMember[] = useMemo(
        () =>
            callingExtensions
                .map(
                    ({ extension, callingEndTime }: CallingMemberExtension) => {
                        const contact = extension?.get('contact');
                        const name = getFullNameFromContact(contact);

                        // Drop the row once the invitee is already in the
                        // meeting — matched by contact key, else display name.
                        const alreadyInMeeting = meetingMemberContacts.find(
                            (meetingContact: MemberContact) => {
                                if (
                                    meetingContact?.contact?.get('key') &&
                                    contact?.get('key')
                                ) {
                                    return (
                                        meetingContact.contact.get('key') ===
                                        contact.get('key')
                                    );
                                }
                                if (name) {
                                    // Some fragile sorcery this, it seems — a
                                    // pure copy-paste from the legacy component.
                                    // Would probably be healthy to look into it.
                                    const memberDisplayName =
                                        sliceUUIDFromUserName(
                                            meetingContact.member?.get(
                                                'displayName',
                                            ),
                                        );
                                    return memberDisplayName === name;
                                }
                                return false;
                            },
                        );

                        // …and once the ring is stale: the invitee left under
                        // CALLING_DURATION ago (a quick re-invite flicker).
                        const memberExtension = allMemberExtensions.find(
                            (memberExt) =>
                                memberExt?.extension?.get('key') ===
                                extension?.get('key'),
                        );
                        const callIsShort =
                            callingEndTime - (memberExtension?.leftTime || 0) <
                            CALLING_DURATION;

                        const callShouldRender =
                            !alreadyInMeeting && !callIsShort;

                        return callShouldRender
                            ? { key: contact?.get('key'), name, contact }
                            : null;
                    },
                )
                .filter(Boolean) as CallingMember[],
        [callingExtensions, meetingMemberContacts, allMemberExtensions],
    );

    // Single pass shared by every consumer that needs the room/guest split
    // (previously duplicated as members.filter(...) in both
    // MeetingParticipantsList and MeetingRoomMembersView).
    const { roomMembers, guestMembers } = useMemo(() => {
        const room: MeetingMember[] = [];
        const guests: MeetingMember[] = [];
        for (const member of members) {
            (member.isGuest ? guests : room).push(member);
        }
        return { roomMembers: room, guestMembers: guests };
    }, [members]);

    return {
        members,
        roomMembers,
        guestMembers,
        callingMembers,
        count: meetingMembers.size,
        closeMembersList,
    };
};

export default useMeetingMembers;
